import { forwardRef } from "react";

import { withSubComponents } from "../../utilities/withSubComponents";
import { InputBaseProps } from "../InputBaseProps";
import { InvalidValue } from "../InvalidValue";
import { RefComponent } from "../RefComponent";
import { Placeholder } from "../Textbox/Placeholder";
import { Field } from "./Field";
import { Input } from "./Input";

/**
 * The `SsnTextbox` component properties.
 */
export interface SsnTextboxProps extends Omit<InputBaseProps<string | InvalidValue | null>, "prefix"> {
  /**
   * Indicates whether the input should mask the entered value.
   */

  readonly masked?: boolean;
  /**
   * The character to display for unfilled portions of the mask. Default is `#`.
   */
  readonly placeholderChar?: string;

  /**
   * Indicates whether the input should display a toggle button to show or hide the masked value.
   */
  readonly showToggle?: boolean;
}

/**
 * The `SsnTextbox` component sub components.
 */
export interface SsnTextboxSubComponents {
  /**
   * A React Hook Form compatible SsnTextbox Field component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the SsnTextbox which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * An input component which allows the user to enter a SSN.
 */
export const SsnTextbox = withSubComponents<RefComponent<HTMLInputElement, SsnTextboxProps>, SsnTextboxSubComponents>(
  {
    Field,
    Placeholder,
  },
  forwardRef<HTMLInputElement, SsnTextboxProps>(function SsnTextbox(props, ref) {
    return <Input {...props} ref={ref} />;
  }),
);
